import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const PageHeaderContext = createContext(null);

/**
 * header config shape (soft contract):
 * {
 *   title: string,
 *   subtitle?: string,
 *   showBack?: boolean,
 *   onBack?: () => void,
 *   backTo?: string,
 *   actions?: ReactNode,
 *   editableTitle?: {
 *     value: string,
 *     onSave: (newValue: string) => Promise<void> | void,
 *     onCancel?: () => void,
 *     placeholder?: string,
 *   },
 * }
 */
export const PageHeaderProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [header, setHeader] = useState(null);

  // route change 시 헤더가 "지워졌다가" 다시 세팅되는 깜빡임/누락을 방지하기 위해
  // - 무조건 초기화(setHeader(null)) 하지 않는다.
  // - 대신, 헤더가 특정 path에 묶여있고(pathTag), route가 바뀌었는데도 새 헤더가 안 올라오는 경우에만 정리한다.
  useEffect(() => {
    setHeader((prev) => {
      if (!prev) return prev;
      if (prev.__path && prev.__path !== location.pathname) return null;
      return prev;
    });
  }, [location.pathname]);

  const api = useMemo(() => {
    const clearHeader = () => setHeader(null);
    const setPageHeader = (config) => {
      if (!config) return setHeader(null);
      setHeader({
        __path: location.pathname,
        title: config.title || "",
        subtitle: config.subtitle || "",
        showBack: Boolean(config.showBack),
        backTo: config.backTo || "",
        onBack:
          typeof config.onBack === "function"
            ? config.onBack
            : config.backTo
              ? () => navigate(config.backTo)
              : Boolean(config.showBack)
                ? () => navigate(-1)
                : null,
        actions: config.actions || null,
        editableTitle: config.editableTitle || null,
      });
    };

    return { header, setPageHeader, clearHeader };
  }, [header, navigate, location.pathname]);

  return <PageHeaderContext.Provider value={api}>{children}</PageHeaderContext.Provider>;
};

export const usePageHeaderApi = () => {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) {
    throw new Error("usePageHeaderApi must be used within PageHeaderProvider");
  }
  return ctx;
};

export const usePageHeader = (config, deps = []) => {
  const { setPageHeader, clearHeader } = usePageHeaderApi();
  // useLayoutEffect를 사용하여 DOM 업데이트 전에 헤더를 설정하여 레이아웃 깜빡임 방지
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    setPageHeader(config);
    return () => clearHeader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};


